import {
  AssistantModalPrimitive,
  AuiIf,
  ActionBarPrimitive,
  AttachmentPrimitive,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  type SourceMessagePartProps,
  useThreadViewport,
} from "@assistant-ui/react";
import {
  ArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from "lucide-react";
import {
  type ComponentProps,
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import {
  OpenInNewOutlinedIcon,
  SendOutlinedIcon,
  StopCircleOutlinedIcon,
  ThumbDownFilledIcon,
  ThumbDownOutlinedIcon,
  ThumbUpFilledIcon,
  ThumbUpOutlinedIcon,
} from "@/components/icons/chat-header-icons";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const THREAD_ROOT_CLASS = "aui-root aui-thread-root @container flex h-full w-full flex-col bg-[#FFFFFF]";
const THREAD_VIEWPORT_CLASS =
  "aui-thread-viewport relative flex w-full flex-1 flex-col overflow-x-hidden overflow-y-scroll scroll-smooth bg-[#FFFFFF]";
const THREAD_VIEWPORT_FOOTER_CLASS =
  "aui-thread-viewport-footer sticky bottom-0 box-border flex w-full flex-col items-center gap-[12px] self-stretch overflow-visible bg-transparent px-[7px] py-[16px] relative";
const MODAL_ANCHOR_CLASS = "aui-root aui-modal-anchor aui-chat-launcher-anchor";
const THREAD_SCROLL_TO_BOTTOM_STYLE: CSSProperties = {
  borderRadius: "50%",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  display: "inline-flex",
};
const THREAD_SCROLL_TO_BOTTOM_BUTTON_CLASS =
  "group aui-thread-scroll-to-bottom absolute bottom-full left-1/2 z-10 inline-flex size-[40px] -translate-x-1/2 items-center justify-center rounded-full text-[#001823] transition-colors focus-visible:outline-none disabled:invisible [&_svg]:size-[24px]";
const THREAD_SCROLL_TO_BOTTOM_FOCUS_CLASS =
  "pointer-events-none absolute left-[-2px] top-[-2px] h-[40px] w-[40px] rounded-[24px] border-2 border-[#001823] opacity-0 group-focus-visible:opacity-100";
const THREAD_SCROLL_TO_BOTTOM_TOOLTIP_CLASS =
  "inline-flex items-start bg-[#001823] text-[#FFFFFF]";
const THREAD_SCROLL_TO_BOTTOM_TOOLTIP_ARROW_CLASS = "bg-[#001823] fill-[#001823]";
const SOURCE_CHIP_CLASS =
  "aui-source-chip mt-2 inline-flex min-h-[24px] max-w-full cursor-pointer items-center justify-center rounded-[100px] border border-[var(--components-chip-defaultEnabledBorder,#B2BBBF)] bg-[rgba(0,24,35,0.08)] p-[4px] font-['Roboto',sans-serif] text-[12px] font-medium leading-[14px] tracking-[0px] text-[#5E7079] no-underline transition-[background-color,border-color,color,box-shadow] duration-150 hover:bg-[rgba(0,24,35,0.12)] hover:text-[#001823] focus:border-[#001823] focus:bg-[rgba(0,0,0,0.12)] focus:text-[#001823] focus:outline-none focus:ring-2 focus:ring-[#001823] focus:ring-offset-0 focus-visible:border-[#001823] focus-visible:bg-[rgba(0,0,0,0.12)] focus-visible:text-[#001823] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-0";
const SOURCE_CHIP_NUMBERED_CLASS = "min-w-[34px] gap-[4px]";
const SOURCE_CHIP_ICON_ONLY_CLASS = "min-w-[24px] gap-0";
const SOURCE_TOOLTIP_CLASS =
  "inline-flex items-start rounded-[4px] bg-[#001823] px-[8px] py-[4px] font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#FFFFFF] shadow-[0_1px_4px_rgba(0,0,0,0.25)]";
const SOURCE_TOOLTIP_ARROW_CLASS = "bg-[#001823] fill-[#001823]";
const SOURCE_DISCLOSURE_CLASS =
  "aui-source-disclosure clear-both flex w-full min-w-0 max-w-full flex-col items-start self-stretch";
const SOURCE_CARD_CLASS =
  "relative mb-[8px] mt-[8px] box-border flex w-full min-w-0 max-w-[336px] flex-col items-start gap-[8px] overflow-hidden rounded-[8px] border border-[rgba(0,24,35,0.12)] bg-[#FFFFFF] p-[16px]";
const SOURCE_CARD_TITLE_CLASS =
  "font-['Roboto',sans-serif] text-[12px] font-bold leading-[16px] tracking-[0px] text-[#001823]";
// UOPX MUI IconButton: the close affordance is circular — hover/focus tint
// and the focus indicator are fully rounded (VIAF-426).
const SOURCE_CARD_CLOSE_CLASS =
  "aui-source-card-close absolute right-[14px] top-[13px] inline-flex size-[16px] items-center justify-center rounded-full text-[rgba(0,24,35,0.56)] hover:bg-[rgba(0,24,35,0.12)] hover:text-[#001823] focus:outline-none focus-visible:outline-none focus-visible:bg-[rgba(0,24,35,0.12)] focus-visible:ring-2 focus-visible:ring-[#001823]";
const SOURCE_CARD_LINK_CLASS =
  "block w-full min-w-0 max-w-full font-['Roboto',sans-serif] text-[12px] font-normal leading-[18px] tracking-[0px] text-[#30718D] underline underline-offset-[2px] [overflow-wrap:anywhere] hover:text-[#001823] focus:outline-none focus:shadow-[0_0_0_2px_#001823] focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#001823]";

const HOME_SUGGESTION_BUTTON_CLASS =
  "aui-suggestion-chip flex h-auto min-h-[32px] items-center self-stretch whitespace-normal rounded-full border border-[#B2BBBF] bg-white px-[10px] py-[4px] font-['Roboto'] text-[13px] font-normal leading-[18px] tracking-[0.16px] text-[#001823] transition-colors hover:bg-[rgba(0,0,0,0.12)] focus-visible:outline-none focus-visible:bg-[rgba(0,0,0,0.12)] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2";

const COMPOSER_ROOT_CLASS = "aui-composer-root relative flex w-full flex-col items-start gap-[8px]";
const HOME_COMPOSER_ROOT_CLASS = "max-w-[360px]";
const THREAD_COMPOSER_ROOT_CLASS = "w-full max-w-[376px]";
const COMPOSER_FIELD_CLASS =
  "aui-composer-field group flex min-h-[48px] max-h-[144px] w-full items-end gap-2 overflow-hidden rounded-[24px] border border-[#a8b0b7] bg-[#ffffff] shadow-[0_4px_8px_0_rgba(0,24,35,0.08)] transition-colors hover:border-[#001823] focus-within:!border-[#001823] focus-within:ring-2 focus-within:ring-[#001823] focus-within:ring-offset-2";
const COMPOSER_FIELD_COMPACT_CLASS = "h-[48px]";
const COMPOSER_FIELD_ERROR_CLASS =
  "!border-[#b21f13] hover:!border-[#b21f13] focus-within:!border-[#b21f13]";
const COMPOSER_FIELD_DISABLED_CLASS = "pointer-events-none border-[#d1d5db] bg-[#f5f5f5]";
const COMPOSER_INPUT_CLASS =
  "aui-composer-input min-h-[24px] max-h-[32px] grow shrink-0 basis-0 resize-none self-stretch border-0 bg-transparent px-[16px] py-3 font-['Roboto',sans-serif] text-[16px] font-normal not-italic leading-[24px] tracking-[0.15px] text-[#001823] outline-none placeholder:text-[#5e7079] focus-visible:outline-none focus-visible:ring-0 group-hover:placeholder:text-[#001823] overflow-hidden";
const COMPOSER_INPUT_EXPANDED_CLASS = "box-border min-h-[48px] max-h-[142px] self-auto overflow-y-auto";
const COMPOSER_INPUT_DISABLED_CLASS = "text-[#5e7079] placeholder:text-[#5e7079]";
const COMPOSER_ACTIONS_CLASS = "flex shrink-0 self-stretch items-end pb-[7px] pr-[8px] pt-[8px]";
const COMPOSER_ACTION_BUTTON_CLASS =
  "mb-0 size-[34px] shrink-0 rounded-full p-[5px] text-[var(--composer-action-color)] transition-colors hover:bg-[rgba(0,24,35,0.12)] hover:text-[var(--composer-action-hover-color)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1f2937] focus-visible:ring-offset-2 disabled:opacity-100 group-hover:text-[var(--composer-action-hover-color)] group-focus-within:!text-[var(--composer-action-focus-color)]";
const COMPOSER_ERROR_CLASS =
  "aui-composer-error flex items-center gap-[4px] px-[2px] font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#b21f13]";

type ComposerActionStyle = CSSProperties & {
  "--composer-action-color": string;
  "--composer-action-hover-color": string;
  "--composer-action-focus-color": string;
};

const COMPOSER_ACTION_DEFAULT_STYLE: ComposerActionStyle = {
  "--composer-action-color": "#5E7079",
  "--composer-action-hover-color": "#001823",
  "--composer-action-focus-color": "#001823",
};
const COMPOSER_ACTION_ACTIVE_STYLE: ComposerActionStyle = {
  "--composer-action-color": "#DB3725",
  "--composer-action-hover-color": "#DB3725",
  "--composer-action-focus-color": "#DB3725",
};
const COMPOSER_ACTION_DISABLED_STYLE: ComposerActionStyle = {
  "--composer-action-color": "#00182361",
  "--composer-action-hover-color": "#00182361",
  "--composer-action-focus-color": "#00182361",
};

const MESSAGE_ERROR_CLASS =
  "aui-message-error-root mt-2 flex w-full max-w-[367px] items-center gap-2 py-1 font-['Roboto',sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-[#b21f13]";
const ASSISTANT_MESSAGE_ROOT_CLASS =
  "aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto flex w-[376px] max-w-[calc(100%-16px)] animate-in items-start gap-[12px] duration-150";
const USER_MESSAGE_ROOT_CLASS =
  "aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto flex max-w-[692px] flex-[1_0_0] animate-in flex-col items-center gap-[24px] self-stretch p-[16px] duration-150";
const ACTION_BAR_CLASS = "mt-1 flex min-h-4 items-center gap-1";
const RATING_BUTTON_CLASS =
  "aui-rating-button inline-flex h-[24px] w-[24px] items-center justify-center rounded-full p-0 text-[rgba(0,24,35,0.56)] hover:bg-[rgba(0,24,35,0.12)] focus-visible:outline-none focus-visible:bg-[rgba(0,24,35,0.12)] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2 data-[submitted=true]:text-[rgba(0,24,35,0.56)]";
const RATING_CONTROLS_CLASS = "flex flex-wrap content-center items-center gap-[4px] self-stretch";
const RATING_ICON_FRAME_CLASS = "flex items-center justify-center";
const RATING_ICON_CLASS = "h-[16.667px] w-[18.333px] shrink-0";
const BRANCH_PICKER_CLASS =
  "aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-muted-foreground text-xs";

export const SuperPhoebeModalRoot: FC<ComponentProps<typeof AssistantModalPrimitive.Root>> = (props) => (
  <AssistantModalPrimitive.Root {...props} />
);

export const SuperPhoebeModalAnchor: FC<ComponentProps<typeof AssistantModalPrimitive.Anchor>> = ({
  className,
  ...props
}) => (
  <AssistantModalPrimitive.Anchor className={cn(MODAL_ANCHOR_CLASS, className)} {...props} />
);

export const SuperPhoebeModalTrigger: FC<ComponentProps<typeof AssistantModalPrimitive.Trigger>> = (props) => (
  <AssistantModalPrimitive.Trigger {...props} />
);

export const SuperPhoebeModalContent: FC<ComponentProps<typeof AssistantModalPrimitive.Content>> = ({
  className,
  ...props
}) => (
  <AssistantModalPrimitive.Content className={className} {...props} />
);

export const SuperPhoebeThreadRoot: FC<ComponentProps<typeof ThreadPrimitive.Root>> = ({
  className,
  ...props
}) => (
  <ThreadPrimitive.Root className={cn(THREAD_ROOT_CLASS, className)} {...props} />
);

export const SuperPhoebeThreadViewport: FC<ComponentProps<typeof ThreadPrimitive.Viewport>> = ({
  className,
  ...props
}) => (
  <ThreadPrimitive.Viewport className={cn(THREAD_VIEWPORT_CLASS, className)} {...props} />
);

export const SuperPhoebeViewportFooter: FC<ComponentProps<typeof ThreadPrimitive.ViewportFooter>> = ({
  className,
  ...props
}) => (
  <ThreadPrimitive.ViewportFooter className={cn(THREAD_VIEWPORT_FOOTER_CLASS, className)} {...props} />
);

export const SuperPhoebeMessages: FC<ComponentProps<typeof ThreadPrimitive.Messages>> = (props) => (
  <ThreadPrimitive.Messages {...props} />
);

export const SuperPhoebeScrollToBottom: FC = () => {
  const isAtBottom = useThreadViewport((state) => state.isAtBottom);
  const scrollToBottom = useThreadViewport((state) => state.scrollToBottom);
  const [isHovered, setIsHovered] = useState(false);
  const [hasBeenRevealed, setHasBeenRevealed] = useState(false);
  const isVisuallyActive = isHovered;
  const isDisabled = isAtBottom && !hasBeenRevealed;
  const clearVisualActive = () => setIsHovered(false);

  useEffect(() => {
    if (!isAtBottom) setHasBeenRevealed(true);
  }, [isAtBottom]);

  return (
    <Tooltip onOpenChange={(open) => {
      setIsHovered(open);
    }}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Scroll to bottom"
          data-testid="aichat_window_scroll-to-bottom"
          disabled={isDisabled}
          className={THREAD_SCROLL_TO_BOTTOM_BUTTON_CLASS}
          style={{
            ...THREAD_SCROLL_TO_BOTTOM_STYLE,
            backgroundColor: isVisuallyActive ? "rgb(245, 246, 246)" : "#ffffff",
          }}
          onPointerEnter={() => setIsHovered(true)}
          onPointerMove={() => setIsHovered(true)}
          onPointerLeave={clearVisualActive}
          onMouseEnter={() => setIsHovered(true)}
          onMouseMove={() => setIsHovered(true)}
          onMouseLeave={clearVisualActive}
          onFocus={() => setIsHovered(true)}
          onBlur={clearVisualActive}
          onClick={() => {
            setHasBeenRevealed(false);
            clearVisualActive();
            scrollToBottom({ behavior: "smooth" });
          }}
        >
          <span aria-hidden="true" className={THREAD_SCROLL_TO_BOTTOM_FOCUS_CLASS} />
          <ArrowDownIcon aria-hidden="true" />
          <span className="aui-sr-only sr-only">Scroll to bottom</span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        className={THREAD_SCROLL_TO_BOTTOM_TOOLTIP_CLASS}
        arrowClassName={THREAD_SCROLL_TO_BOTTOM_TOOLTIP_ARROW_CLASS}
      >
        Scroll to bottom
      </TooltipContent>
    </Tooltip>
  );
};

export const SuperPhoebeSuggestionChip: FC<{ text: string }> = ({ text }) => (
  <div className="fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200">
    <ThreadPrimitive.Suggestion asChild prompt={text} send>
      <Button
        variant="ghost"
        className={HOME_SUGGESTION_BUTTON_CLASS}
        style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
        data-testid="aichat_window_suggestion-chip"
      >
        <span className="min-w-0 whitespace-normal break-words">{text}</span>
      </Button>
    </ThreadPrimitive.Suggestion>
  </div>
);

export const SuperPhoebeComposerRoot: FC<
  ComponentProps<typeof ComposerPrimitive.Root> & { variant?: "home" | "thread" }
> = ({ className, variant = "thread", ...props }) => (
  <ComposerPrimitive.Root
    className={cn(
      COMPOSER_ROOT_CLASS,
      variant === "home" ? HOME_COMPOSER_ROOT_CLASS : THREAD_COMPOSER_ROOT_CLASS,
      className,
    )}
    {...props}
  />
);

export const SuperPhoebeComposerField: FC<{
  children: ReactNode;
  isExpanded?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
}> = ({ children, isExpanded = false, isDisabled = false, hasError = false }) => (
  <div
    className={cn(
      COMPOSER_FIELD_CLASS,
      !isExpanded && COMPOSER_FIELD_COMPACT_CLASS,
      hasError && COMPOSER_FIELD_ERROR_CLASS,
      isDisabled && COMPOSER_FIELD_DISABLED_CLASS,
    )}
    aria-disabled={isDisabled || undefined}
  >
    {children}
  </div>
);

export const SuperPhoebeComposerInput: FC<
  ComponentProps<typeof ComposerPrimitive.Input> & {
    isExpanded?: boolean;
    isDisabled?: boolean;
  }
> = ({ className, isExpanded = false, isDisabled = false, style, ...props }) => (
  <ComposerPrimitive.Input
    className={cn(
      COMPOSER_INPUT_CLASS,
      isExpanded && COMPOSER_INPUT_EXPANDED_CLASS,
      isDisabled && COMPOSER_INPUT_DISABLED_CLASS,
      className,
    )}
    style={{ outline: "none", boxShadow: "none", fontFeatureSettings: "'liga' off, 'clig' off", ...style }}
    {...props}
  />
);

export const SuperPhoebeComposerActions: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={COMPOSER_ACTIONS_CLASS}>{children}</div>
);

export const SuperPhoebeComposerAction: FC<{ isActive?: boolean; isDisabled?: boolean; isSendBlocked?: boolean }> = ({
  isActive = false,
  isDisabled = false,
  isSendBlocked = false,
}) => {
  const actionStyle = isActive
    ? COMPOSER_ACTION_ACTIVE_STYLE
    : isDisabled || isSendBlocked
      ? COMPOSER_ACTION_DISABLED_STYLE
      : COMPOSER_ACTION_DEFAULT_STYLE;

  return (
    <>
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip="Send"
            side="bottom"
            type="button"
            variant="ghost"
            size="icon"
            className={cn("aui-composer-send hover:opacity-80", COMPOSER_ACTION_BUTTON_CLASS)}
            style={actionStyle}
            aria-label="Send"
            data-testid="aichat_window_send-message"
            disabled={isSendBlocked || isDisabled}
          >
            <SendOutlinedIcon className="size-[24px]" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </AuiIf>
      <AuiIf condition={(s) => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <TooltipIconButton
            tooltip="Stop generating"
            side="bottom"
            type="button"
            variant="ghost"
            size="icon"
            className={cn("aui-composer-cancel", COMPOSER_ACTION_BUTTON_CLASS)}
            style={COMPOSER_ACTION_ACTIVE_STYLE}
            aria-label="Stop generating"
          >
            <StopCircleOutlinedIcon className="size-[24px]" />
          </TooltipIconButton>
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </>
  );
};

export const SuperPhoebeComposerError: FC<{
  id: string;
  iconSrc: string;
  children: ReactNode;
}> = ({ id, iconSrc, children }) => (
  <p id={id} className={COMPOSER_ERROR_CLASS}>
    <img src={iconSrc} alt="Error" className="size-[16px] shrink-0" />
    {children}
  </p>
);

export const SuperPhoebeAssistantMessageRoot: FC<ComponentProps<typeof MessagePrimitive.Root>> = ({
  className,
  ...props
}) => (
  <MessagePrimitive.Root
    className={cn(ASSISTANT_MESSAGE_ROOT_CLASS, className)}
    data-role="assistant"
    data-testid="aichat_window_message-phoebe"
    {...props}
  />
);

export const SuperPhoebeUserMessageRoot: FC<ComponentProps<typeof MessagePrimitive.Root>> = ({
  className,
  ...props
}) => (
  <MessagePrimitive.Root
    className={cn(USER_MESSAGE_ROOT_CLASS, className)}
    data-role="user"
    data-testid="aichat_window_message-user"
    {...props}
  />
);

export const SuperPhoebeMessageRoot: FC<ComponentProps<typeof MessagePrimitive.Root>> = ({
  className,
  ...props
}) => <MessagePrimitive.Root className={className} {...props} />;

export const SuperPhoebeMessageParts: FC<ComponentProps<typeof MessagePrimitive.Parts>> = ({
  components,
  ...props
}) => (
  <MessagePrimitive.Parts
    components={{
      Source: SuperPhoebeSourceChip,
      ...(components ?? {}),
    }}
    {...props}
  />
);

export const SuperPhoebeMessageError: FC<{ iconSrc: string }> = ({ iconSrc }) => (
  <MessagePrimitive.Error>
    <ErrorPrimitive.Root
      className={MESSAGE_ERROR_CLASS}
      role="alert"
      data-testid="aichat_window_error_response"
    >
      <img src={iconSrc} alt="Error" className="size-[16.667px] shrink-0" />
      <span className="min-w-0 flex-1 wrap-break-word" style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}>
        I'm having trouble connecting right now. Please try again or call 1-866-766-0766 for additional assistance.
      </span>
      <ErrorPrimitive.Message className="sr-only" />
    </ErrorPrimitive.Root>
  </MessagePrimitive.Error>
);

export type SuperPhoebeSourceChipProps = SourceMessagePartProps & {
  showNumber?: boolean;
};

type SourceCardLink = {
  title: string;
  url: string;
};

export const SuperPhoebeSourceChip: FC<SuperPhoebeSourceChipProps> = ({
  id,
  providerMetadata,
  title,
  url,
  showNumber = true,
}) => {
  const cardId = useId();
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const sourceName = title?.trim() || sourceTitleFromUrl(url) || safeSourceHost(url) || id;
  const sourceGroup = getPhoebeSourceGroupMetadata(providerMetadata, { title: sourceName, url });
  if (sourceGroup.index > 0) return null;

  const closeCard = () => {
    triggerButtonRef.current?.focus();
    setIsCardOpen(false);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeCard();
    }
  };
  const visibleLabel = String(sourceGroup.count);
  const accessibleLabel = sourceGroup.count === 1 ? "View source" : `View ${sourceGroup.count} sources`;
  const cardTitle = sourceGroup.count === 1 ? "Source" : "Sources";
  const collapseLabel = sourceGroup.count === 1 ? "Collapse source" : "Collapse sources";

  return (
    <div className={SOURCE_DISCLOSURE_CLASS} onKeyDown={handleKeyDown}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            ref={triggerButtonRef}
            aria-controls={isCardOpen ? cardId : undefined}
            aria-expanded={isCardOpen}
            aria-label={accessibleLabel}
            className={cn(SOURCE_CHIP_CLASS, showNumber ? SOURCE_CHIP_NUMBERED_CLASS : SOURCE_CHIP_ICON_ONLY_CLASS)}
            data-show-number={showNumber ? "true" : "false"}
            data-testid="aichat_window_source-chip"
            onClick={() => setIsCardOpen((open) => !open)}
          >
            <LinkOutlinedSourceIcon className="size-[14px] shrink-0" aria-hidden="true" />
            {showNumber ? (
              <span aria-hidden="true" className="min-w-0 truncate">
                {visibleLabel}
              </span>
            ) : null}
          </button>
        </TooltipTrigger>
        <TooltipContent
          arrowClassName={SOURCE_TOOLTIP_ARROW_CLASS}
          className={SOURCE_TOOLTIP_CLASS}
          side="top"
        >
          {accessibleLabel}
        </TooltipContent>
      </Tooltip>
      {isCardOpen ? (
        <SuperPhoebeSourceCard
          collapseLabel={collapseLabel}
          id={cardId}
          onClose={closeCard}
          sources={sourceGroup.sources}
          title={cardTitle}
        />
      ) : null}
    </div>
  );
};

const SuperPhoebeSourceCard: FC<{
  collapseLabel: string;
  id: string;
  onClose: () => void;
  sources: SourceCardLink[];
  title: string;
}> = ({ collapseLabel, id, onClose, sources, title }) => (
  <div
    aria-labelledby={`${id}-title`}
    className={SOURCE_CARD_CLASS}
    data-testid="aichat_window_source-card"
    id={id}
    role="region"
  >
    <h3 className={SOURCE_CARD_TITLE_CLASS} id={`${id}-title`}>
      {title}
    </h3>
    <button
      type="button"
      aria-label={collapseLabel}
      className={SOURCE_CARD_CLOSE_CLASS}
      onClick={onClose}
    >
      <XIcon className="size-[12px]" aria-hidden="true" />
    </button>
    <ul className="flex w-full min-w-0 list-none flex-col gap-[8px] p-0">
      {sources.map((source) => (
        <li key={source.url} className="w-full min-w-0">
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${source.title} Opens in new window`}
            className={SOURCE_CARD_LINK_CLASS}
            title={source.title}
          >
            <span>{source.title}</span>
            <span aria-label="Opens in new window" className="ml-[3px] inline-flex size-[12px] align-[-2px]" role="img">
              <OpenInNewOutlinedIcon className="size-[12px]" aria-hidden="true" />
            </span>
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const LinkOutlinedSourceIcon: FC<{ className?: string; "aria-hidden"?: boolean }> = ({
  className,
  "aria-hidden": ariaHidden,
}) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden={ariaHidden}
  >
    <path
      d="M9.91699 4.08398H7.58366V5.25065H9.91699C10.8795 5.25065 11.667 6.03815 11.667 7.00065C11.667 7.96315 10.8795 8.75065 9.91699 8.75065H7.58366V9.91732H9.91699C11.527 9.91732 12.8337 8.61065 12.8337 7.00065C12.8337 5.39065 11.527 4.08398 9.91699 4.08398ZM6.41699 8.75065H4.08366C3.12116 8.75065 2.33366 7.96315 2.33366 7.00065C2.33366 6.03815 3.12116 5.25065 4.08366 5.25065H6.41699V4.08398H4.08366C2.47366 4.08398 1.16699 5.39065 1.16699 7.00065C1.16699 8.61065 2.47366 9.91732 4.08366 9.91732H6.41699V8.75065ZM4.66699 6.41732H9.33366V7.58398H4.66699V6.41732Z"
      fill="currentColor"
    />
  </svg>
);

export const SuperPhoebeActionBar: FC<ComponentProps<typeof ActionBarPrimitive.Root>> = ({
  className,
  ...props
}) => (
  <ActionBarPrimitive.Root
    hideWhenRunning
    className={cn(ACTION_BAR_CLASS, className)}
    {...props}
  />
);

export const SuperPhoebeRatingControls: FC = () => {
  const [rating, setRating] = useState<"helpful" | "not_helpful" | null>(null);

  return (
    <div className={RATING_CONTROLS_CLASS} aria-label="Rate response">
      <ActionBarPrimitive.FeedbackPositive asChild>
        <TooltipIconButton
          tooltip="Good response"
          variant="ghost"
          size="icon"
          aria-label="Good response"
          aria-pressed={rating === "helpful"}
          data-testid="aichat_window_thumbs_up"
          data-submitted={rating === "helpful" ? "true" : undefined}
          style={{ borderRadius: "9999px" }}
          className={RATING_BUTTON_CLASS}
          onClick={() => setRating((current) => (current === "helpful" ? null : "helpful"))}
        >
          <span className={RATING_ICON_FRAME_CLASS} aria-hidden="true">
            {rating === "helpful" ? (
              <ThumbUpFilledIcon className={RATING_ICON_CLASS} />
            ) : (
              <ThumbUpOutlinedIcon className={RATING_ICON_CLASS} />
            )}
          </span>
        </TooltipIconButton>
      </ActionBarPrimitive.FeedbackPositive>
      <ActionBarPrimitive.FeedbackNegative asChild>
        <TooltipIconButton
          tooltip="Bad response"
          variant="ghost"
          size="icon"
          aria-label="Bad response"
          aria-pressed={rating === "not_helpful"}
          data-testid="aichat_window_thumbs_down"
          data-submitted={rating === "not_helpful" ? "true" : undefined}
          style={{ borderRadius: "9999px" }}
          className={RATING_BUTTON_CLASS}
          onClick={() => setRating((current) => (current === "not_helpful" ? null : "not_helpful"))}
        >
          <span className={RATING_ICON_FRAME_CLASS} aria-hidden="true">
            {rating === "not_helpful" ? (
              <ThumbDownFilledIcon className={RATING_ICON_CLASS} />
            ) : (
              <ThumbDownOutlinedIcon className={RATING_ICON_CLASS} />
            )}
          </span>
        </TooltipIconButton>
      </ActionBarPrimitive.FeedbackNegative>
    </div>
  );
};

export const SuperPhoebeBranchPicker: FC<ComponentProps<typeof BranchPickerPrimitive.Root>> = ({
  className,
  ...rest
}) => (
  <BranchPickerPrimitive.Root
    hideWhenSingleBranch
    className={cn(BRANCH_PICKER_CLASS, className)}
    {...rest}
  >
    <BranchPickerPrimitive.Previous asChild>
      <TooltipIconButton tooltip="Previous response">
        <ChevronLeftIcon />
      </TooltipIconButton>
    </BranchPickerPrimitive.Previous>
    <span className="aui-branch-picker-state font-medium">
      <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
    </span>
    <BranchPickerPrimitive.Next asChild>
      <TooltipIconButton tooltip="Next response">
        <ChevronRightIcon />
      </TooltipIconButton>
    </BranchPickerPrimitive.Next>
  </BranchPickerPrimitive.Root>
);

export const SuperPhoebeAttachmentRoot: FC<ComponentProps<typeof AttachmentPrimitive.Root>> = ({
  className,
  ...props
}) => (
  <AttachmentPrimitive.Root className={className} {...props} />
);

export const SuperPhoebeAttachmentName: FC<ComponentProps<typeof AttachmentPrimitive.Name>> = (props) => (
  <AttachmentPrimitive.Name {...props} />
);

export const SuperPhoebeAttachmentRemove: FC<ComponentProps<typeof AttachmentPrimitive.Remove>> = (props) => (
  <AttachmentPrimitive.Remove {...props} />
);

export const SuperPhoebeMessageAttachmentsPrimitive: FC<
  ComponentProps<typeof MessagePrimitive.Attachments>
> = (props) => (
  <MessagePrimitive.Attachments {...props} />
);

export const SuperPhoebeComposerAttachmentsPrimitive: FC<
  ComponentProps<typeof ComposerPrimitive.Attachments>
> = (props) => (
  <ComposerPrimitive.Attachments {...props} />
);

export const SuperPhoebeComposerAddAttachmentPrimitive: FC<
  ComponentProps<typeof ComposerPrimitive.AddAttachment>
> = (props) => (
  <ComposerPrimitive.AddAttachment {...props} />
);

export const SuperPhoebeEditComposer: FC = () => (
  <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
    <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
      <ComposerPrimitive.Input
        className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none"
        aria-label="Edit message"
        autoFocus
      />
      <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
        <ComposerPrimitive.Cancel asChild>
          <Button variant="ghost" size="sm">Cancel</Button>
        </ComposerPrimitive.Cancel>
        <ComposerPrimitive.Send asChild>
          <Button size="sm">Update</Button>
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  </MessagePrimitive.Root>
);

function safeSourceHost(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function sourceTitleFromUrl(url: string): string | null {
  try {
    const segment = new URL(url).pathname
      .split("/")
      .filter(Boolean)
      .pop()
      ?.replace(/\.(?:html?|aspx?)$/i, "");
    if (!segment) return null;

    const words = segment.split(/[-_]+/).filter(Boolean);
    const titleWords: string[] = [];
    for (let index = 0; index < words.length; index += 1) {
      const word = words[index];
      const nextWord = words[index + 1];
      if (!word) continue;
      if (/^\d+$/.test(word) && nextWord && /^[a-z]$/i.test(nextWord)) {
        titleWords.push(`${word}-${nextWord.toUpperCase()}`);
        index += 1;
        continue;
      }
      titleWords.push(formatSourceTitleWord(word));
    }

    return titleWords.join(" ");
  } catch {
    return null;
  }
}

function formatSourceTitleWord(word: string): string {
  if (/^(?:irs|faq|fafsa|uopx)$/i.test(word)) return word.toUpperCase();
  if (/^\d+$/.test(word)) return word;
  return `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`;
}

function getPhoebeSourceGroupMetadata(
  providerMetadata: SourceMessagePartProps["providerMetadata"] | undefined,
  fallbackSource: SourceCardLink,
): { count: number; index: number; sources: SourceCardLink[] } {
  const metadata = providerMetadata?.phoebe;
  if (!isSourceGroupMetadata(metadata)) {
    return { count: 1, index: 0, sources: [fallbackSource] };
  }
  const sources = normalizeSourceGroupSources(metadata.sourceGroupSources);
  const count = sources.length > 0 ? sources.length : normalizePositiveInteger(metadata.sourceGroupCount, 1);

  return {
    count,
    index: normalizeNonNegativeInteger(metadata.sourceGroupIndex, 0),
    sources: sources.length > 0 ? sources : [fallbackSource],
  };
}

function isSourceGroupMetadata(
  value: unknown,
): value is { sourceGroupCount?: unknown; sourceGroupIndex?: unknown; sourceGroupSources?: unknown } {
  return typeof value === "object" && value !== null;
}

function normalizeSourceGroupSources(value: unknown): SourceCardLink[] {
  if (!Array.isArray(value)) return [];

  const links: SourceCardLink[] = [];
  const seenUrls = new Set<string>();
  for (const item of value) {
    if (!isSourceCardSourceRecord(item)) continue;
    const url = normalizeSourceCardUrl(item.url);
    if (!url || seenUrls.has(url)) continue;

    links.push({
      title: cleanSourceCardTitle(item.title) || sourceTitleFromUrl(url) || safeSourceHost(url) || url,
      url,
    });
    seenUrls.add(url);
  }

  return links;
}

function isSourceCardSourceRecord(value: unknown): value is { title?: unknown; url?: unknown } {
  return typeof value === "object" && value !== null;
}

function normalizeSourceCardUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

function cleanSourceCardTitle(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

function normalizePositiveInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function normalizeNonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : fallback;
}
